import { NavLink, useLocation } from "react-router";
import type { ComponentType } from "react";

type NavItemProps = {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number | string; className?: string }>;
  badge?: React.ReactNode;
  collapsed?: boolean;
  /** Renders as a child of the section above it. Indented when expanded. */
  nested?: boolean;
  /**
   * Overrides NavLink's own matching. A section parent stays highlighted on its
   * detail routes (/clients/:id) but not on a child's route (/clients/people).
   */
  activeWhen?: (pathname: string) => boolean;
};

export function NavItem({
  to,
  label,
  icon: Icon,
  badge,
  collapsed,
  nested,
  activeWhen,
}: NavItemProps) {
  const { pathname } = useLocation();

  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive: matched }) => {
        const isActive = activeWhen ? activeWhen(pathname) : matched;
        return `flex items-center gap-2.5 rounded-md min-h-11 ${
          nested && !collapsed ? "text-[13px]" : "text-[13.5px]"
        } ${
          collapsed
            ? "justify-center px-2 py-2"
            : nested
              ? "pl-9 pr-3 py-2"
              : "px-3 py-2"
        } ${isActive ? "bg-ink text-paper-hi" : "text-ink-2 hover:bg-paper"}`;
      }}
    >
      <Icon size={nested && !collapsed ? 14 : 15} className="shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{label}</span>
          {badge && <span className="shrink-0">{badge}</span>}
        </>
      )}
      {collapsed && badge && (
        <span className="absolute right-1 top-1">{badge}</span>
      )}
    </NavLink>
  );
}
